import React, { FC } from 'react';
import { Button, Text, Skeleton } from '@cubeyou-team/hx-react-components';
import { AI_GRADIENT_PROPERTIES } from '@discover-frontend/constants';

import translations from 'src/lang';
import { useStanleyQueryMembershipSize } from '../../../../../../../../api/hooks';
import { StanleyQuerySuggestion } from '../../../../../../../../api/types';
import { useShareStanleyQuerySuggestion } from '../../../../../../../../hooks/use-share-stanley-query-suggestion';
import { ColoredBooleanString } from './ColoredBooleanString';
import { QueryItemBadge } from './QueryItemBadge';
import { PreWrapText } from './PreWrapText';

const { STANLEY_QUERY: translation } = translations['en-US'];

interface StanleyQueryItemProps {
  query: StanleyQuerySuggestion;
  index: number;
  onUseQuery: (query: StanleyQuerySuggestion) => void;
}

const formatAudienceSize = (size: number) =>
  size.toLocaleString('en-US', { maximumFractionDigits: 0 });

export const StanleyQueryItem: FC<StanleyQueryItemProps> = ({
  query,
  index,
  onUseQuery,
}) => {
  const {
    data: audienceSize,
    isLoading: isSizeLoading,
    isError: isSizeError,
  } = useStanleyQueryMembershipSize(query);
  const { handleShare, canShare, isPreparingShare } =
    useShareStanleyQuerySuggestion(query);

  return (
    <div className="p-4 bg-white rounded flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <QueryItemBadge index={index + 1} />
          <QueryItemBadge complexity={query.complexity} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            icon="icon-share"
            size="small"
            variant="primary"
            disabled={!canShare}
            loading={isPreparingShare}
            onClick={handleShare}
          >
            {translation.BUTTON.SHARE_QUERY}
          </Button>
          <Button
            type="button"
            icon="icon-ai"
            size="small"
            variant="secondary"
            gradient={AI_GRADIENT_PROPERTIES}
            onClick={() => onUseQuery(query)}
          >
            {translation.BUTTON.USE_QUERY}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Text size="x_body" weight="bold">
          {query.title}
        </Text>
        <div className="flex min-w-0 flex-row items-start">
          <Text tag="span" size="x_body" weight="bold" className="shrink-0">
            {translation.MODAL.QUERY_ITEM.LABEL.QUERY}:
          </Text>
          <ColoredBooleanString text={query.boolean_string} />
        </div>
        <div className="flex flex-wrap items-center gap-x-1">
          <Text tag="span" size="x_body" weight="bold">
            {translation.MODAL.QUERY_ITEM.LABEL.SIZE}:
          </Text>
          {isSizeLoading ? (
            <Skeleton speed={2} width={130} height={16} viewBox="0 0 130 16">
              <rect x="0" y="4" rx="4" ry="4" width="130" height="10" />
            </Skeleton>
          ) : isSizeError ? (
            <Text tag="span" size="x_body" weight="regular" className="ml-1">
              —
            </Text>
          ) : audienceSize != null ? (
            <PreWrapText text={formatAudienceSize(audienceSize)} />
          ) : (
            <Text tag="span" size="x_body" weight="regular" className="ml-1">
              —
            </Text>
          )}
        </div>
        <div>
          <Text tag="span" size="x_body" weight="bold">
            {translation.MODAL.QUERY_ITEM.LABEL.TARGET_ANALYSIS}:
          </Text>
          <PreWrapText text={query.description} />
        </div>
      </div>
    </div>
  );
};
